import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  Input,
  Button,
  Spinner,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
} from "reactstrap";
import Swal from "sweetalert2";
import { getFirebaseAuthToken } from "../../../helpers/firebase_helper";
import { Link } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const authHeaders = async () => ({
  "Content-Type": "application/json",
  Authorization: `${await getFirebaseAuthToken()}`,
});

const nationalName = (n) =>
  n?.companyName ||
  `${n?.firstName || ""} ${n?.lastName || ""}`.trim() ||
  n?.email ||
  n?.id;

/**
 * The scopes an admin can grant a partner. These MUST match the backend's
 * PARTNER_SCOPES (src/models/partner.model.ts) — the create endpoint rejects any
 * value it doesn't know, so a typo here fails loudly rather than granting
 * nothing. The two legacy members:* scopes are intentionally omitted (unused).
 */
const ACTIVE_SCOPES = [
  { value: "accounts:read", label: "Accounts — read (validate accounts, link tokens)" },
  { value: "groups:write", label: "Employer groups — write (create / sync groups)" },
  { value: "members:write", label: "Members — write (Members API: add / update / terminate members)" },
  { value: "employees:write", label: "Employees — write (employer-groups API: add / terminate employees)" },
  { value: "commissions:read", label: "Commissions — read" },
  { value: "cards:read", label: "Member cards — read" },
  { value: "drugs:read", label: "Drug pricing — read (drug search, prices, BaxRx+ list)" },
];
const DEFAULT_SCOPES = ACTIVE_SCOPES.map((s) => s.value);

const PROVIDER_LABELS = {
  ddn: "DDN — Discount Drug Network (production)",
  scriptsave: "ScriptSave / APS (adds pre-enrollment discount card)",
};
const SOURCE_LABELS = {
  db: "set here (DB)",
  env: "environment variable",
  default: "default",
};

const fmtDate = (ms) => {
  if (!ms) return null;
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return null;
  }
};

/**
 * Every National, across every page.
 *
 * This is a dropdown: an option that isn't in it may as well not exist. So it
 * has to be the WHOLE list, and the endpoint is paginated with a page size the
 * caller can't change (15). Two ways that has already bitten:
 *
 * - It was called with `page=1`, which is the SECOND page — the route is
 *   0-indexed. With 11 Nationals that skipped past all of them and returned
 *   `{ data: [], total: 11 }`: an empty dropdown that looked exactly like an
 *   account with no Nationals.
 * - Even fixed, a single page silently caps the list at 15. The 16th National
 *   would simply never appear, and nobody would know why.
 *
 * So: read `total` from the first page and keep going until we have them all.
 * Throws on failure — the caller must never render a short list as if it were
 * the whole one.
 */
const fetchAllNationals = async (headers) => {
  const get = async (page) => {
    const res = await fetch(
      `${API_BASE}/dashboard/admin/nationals?page=${page}&query=`,
      { headers }
    );
    if (!res.ok) throw new Error(`Nationals request failed (${res.status})`);
    return res.json();
  };

  const first = await get(0);
  const all = [...(first.data || [])];
  const total = first.total ?? all.length;

  for (let page = 1; all.length < total; page++) {
    const next = await get(page);
    const batch = next.data || [];
    if (batch.length === 0) break; // defensive: never loop forever
    all.push(...batch);
  }

  return all;
};

/**
 * The one-time reveal of a freshly minted (or rotated) API key.
 *
 * The backend stores only the key's hash, so THIS modal is the only place the
 * raw key ever exists. Once it's closed the key is gone for good — the copy
 * button and the blunt warning are the whole point.
 */
const KeyRevealModal = ({ data, onClose }) => {
  const [copied, setCopied] = useState(false);
  if (!data) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      Swal.fire("Copy failed", "Select the key and copy it manually.", "warning");
    }
  };

  return (
    <Modal isOpen backdrop="static" keyboard={false} centered>
      <ModalHeader>
        {data.isRotation ? "New API key" : "Partner created"} — {data.partnerName}
      </ModalHeader>
      <ModalBody>
        <p className="mb-2">
          {data.isRotation
            ? "The previous key has stopped working. Give the partner this new key:"
            : "Copy this API key and send it to the partner over a secure channel:"}
        </p>
        <div className="d-flex gap-2 align-items-stretch">
          <Input
            type="text"
            readOnly
            value={data.apiKey}
            onFocus={(e) => e.target.select()}
            style={{ fontFamily: "monospace" }}
          />
          <Button color={copied ? "success" : "primary"} onClick={copy}>
            {copied ? "Copied!" : "Copy"}
          </Button>
        </div>
        <div className="alert alert-warning mt-3 mb-0 small">
          <strong>This is the only time this key is shown.</strong> We store only
          a hash of it — it can never be retrieved again, not even by an admin. If
          it's lost, rotate the key to issue a new one.
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>
          I've copied it — close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

/**
 * Admin page for the partners integrating with BaxRx (e.g. Avero Health).
 * One row per partner.
 *
 * Three things happen here:
 *
 * - Create a partner and mint its first API key (shown once).
 * - Manage each partner's key: rotate it (issues a new one, kills the old),
 *   deactivate/reactivate, and set its default National (the parent used for
 *   brokers the partner onboards without naming one).
 * - Delete a partner — but ONLY if it never provisioned anything. A partner with
 *   employer groups or employees CANNOT be deleted: those records would keep
 *   pointing at a partner that no longer exists, and nobody could sync, correct
 *   or clean them up. The backend refuses (409) and this page does not even
 *   offer the button — it offers Deactivate, which kills the key and leaves the
 *   data manageable.
 */
const PartnerIntegrations = () => {
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [nationals, setNationals] = useState([]);
  const [selections, setSelections] = useState({}); // partnerId -> nationalId
  const [savingId, setSavingId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // Create-partner form.
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "",
    slug: "",
    scopes: DEFAULT_SCOPES,
  });

  // One-time key reveal (create or rotate).
  const [keyReveal, setKeyReveal] = useState(null);

  // Drug-pricing provider (system-wide setting, DB-backed).
  const [providerConfig, setProviderConfig] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [savingProvider, setSavingProvider] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();

      const [partnersRes, nationals, providerRes] = await Promise.all([
        fetch(`${API_BASE}/dashboard/admin/partners`, { headers }),
        fetchAllNationals(headers),
        fetch(`${API_BASE}/dashboard/admin/settings/drug-pricing`, { headers }),
      ]);

      const partnersJson = partnersRes.ok ? await partnersRes.json() : {};
      const partnerList = partnersJson.partners || [];

      setPartners(partnerList);
      setNationals(nationals);

      if (providerRes.ok) {
        const cfg = await providerRes.json();
        setProviderConfig(cfg);
        setSelectedProvider(cfg.provider || "");
      }

      const initial = {};
      partnerList.forEach((p) => {
        initial[p.id] = p.defaultNationalId || "";
      });
      setSelections(initial);
    } catch (e) {
      // Say what actually failed. A silent empty list is how both of the bugs
      // on this page stayed invisible.
      Swal.fire(
        "Could not load",
        e.message || "Failed to load partner integrations",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleScope = (value) => {
    setForm((f) => ({
      ...f,
      scopes: f.scopes.includes(value)
        ? f.scopes.filter((s) => s !== value)
        : [...f.scopes, value],
    }));
  };

  const createPartner = async () => {
    if (!form.name.trim()) {
      Swal.fire("Name required", "Give the partner a name.", "warning");
      return;
    }
    if (!form.scopes.length) {
      Swal.fire("Pick a scope", "Select at least one scope.", "warning");
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE}/dashboard/admin/partners`, {
        method: "POST",
        headers: await authHeaders(),
        body: JSON.stringify({
          name: form.name.trim(),
          slug: form.slug.trim() || undefined,
          scopes: form.scopes,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Failed to create partner");

      // Show the raw key once, then refresh the list so the new row appears.
      setKeyReveal({
        apiKey: json.apiKey,
        partnerName: json.partner?.name || form.name.trim(),
        isRotation: false,
      });
      setShowCreate(false);
      setForm({ name: "", slug: "", scopes: DEFAULT_SCOPES });
      load();
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to create partner", "error");
    } finally {
      setCreating(false);
    }
  };

  const rotateKey = async (partner) => {
    const { isConfirmed } = await Swal.fire({
      title: `Rotate ${partner.name}'s API key?`,
      html:
        `A new key is issued and the current one <strong>stops working immediately</strong>.<br/><br/>` +
        `The partner must switch to the new key — this is not zero-downtime. ` +
        `Only rotate to recover a lost key or retire a leaked one.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Rotate key",
      confirmButtonColor: "#d33",
    });
    if (!isConfirmed) return;

    setBusyId(partner.id);
    try {
      const res = await fetch(
        `${API_BASE}/dashboard/admin/partners/${partner.id}/rotate-key`,
        { method: "POST", headers: await authHeaders(), body: "{}" }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Failed to rotate key");

      setKeyReveal({
        apiKey: json.apiKey,
        partnerName: partner.name,
        isRotation: true,
      });
      setPartners((prev) =>
        prev.map((p) =>
          p.id === partner.id
            ? { ...p, hasKey: true, rotatedAt: json.rotatedAt || Date.now() }
            : p
        )
      );
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to rotate key", "error");
    } finally {
      setBusyId(null);
    }
  };

  const saveProvider = async () => {
    if (!selectedProvider || selectedProvider === providerConfig?.provider) {
      return;
    }
    const { isConfirmed } = await Swal.fire({
      title: `Switch drug-pricing provider to ${
        PROVIDER_LABELS[selectedProvider] || selectedProvider
      }?`,
      html:
        `This changes the provider for <strong>drug search and the free discount ` +
        `card, system-wide</strong>, effective immediately.<br/><br/>` +
        (selectedProvider === "scriptsave"
          ? `ScriptSave issues its own cardholder ids — moving fully onto it may ` +
            `require a member-id migration.`
          : `DDN is the production provider.`),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Switch provider",
      confirmButtonColor: "#d33",
    });
    if (!isConfirmed) return;

    setSavingProvider(true);
    try {
      const res = await fetch(
        `${API_BASE}/dashboard/admin/settings/drug-pricing`,
        {
          method: "PUT",
          headers: await authHeaders(),
          body: JSON.stringify({ provider: selectedProvider }),
        }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Failed to switch provider");
      setProviderConfig(json);
      setSelectedProvider(json.provider);
      Swal.fire(
        "Provider switched",
        `Now using ${PROVIDER_LABELS[json.provider] || json.provider}.`,
        "success"
      );
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to switch provider", "error");
    } finally {
      setSavingProvider(false);
    }
  };

  const save = async (partnerId) => {
    setSavingId(partnerId);
    try {
      const res = await fetch(
        `${API_BASE}/dashboard/admin/partners/${partnerId}/default-national`,
        {
          method: "PUT",
          headers: await authHeaders(),
          body: JSON.stringify({ nationalId: selections[partnerId] || null }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to save");
      }
      Swal.fire("Saved", "Default National updated", "success");
      setPartners((prev) =>
        prev.map((p) =>
          p.id === partnerId
            ? { ...p, defaultNationalId: selections[partnerId] || null }
            : p
        )
      );
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setSavingId(null);
    }
  };

  const toggleActive = async (partner) => {
    if (partner.active) {
      const { isConfirmed } = await Swal.fire({
        title: `Deactivate ${partner.name}?`,
        html:
          `Their API key stops working immediately — no more syncing.<br/><br/>` +
          `The ${partner.groupsCount} employer group(s) and ${partner.employeesCount} employee(s) ` +
          `they provisioned stay exactly as they are.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Deactivate",
        confirmButtonColor: "#d33",
      });
      if (!isConfirmed) return;
    }

    setBusyId(partner.id);
    try {
      const res = await fetch(
        `${API_BASE}/dashboard/admin/partners/${partner.id}`,
        {
          method: "PATCH",
          headers: await authHeaders(),
          body: JSON.stringify({ active: !partner.active }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to update partner");
      }
      setPartners((prev) =>
        prev.map((p) =>
          p.id === partner.id ? { ...p, active: !partner.active } : p
        )
      );
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to update partner", "error");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (partner) => {
    const { isConfirmed } = await Swal.fire({
      title: `Delete ${partner.name}?`,
      html:
        `This partner has never provisioned anything, so deleting it removes ` +
        `only the partner record and its API key.<br/><br/><strong>This cannot be undone.</strong>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
    });
    if (!isConfirmed) return;

    setBusyId(partner.id);
    try {
      const res = await fetch(
        `${API_BASE}/dashboard/admin/partners/${partner.id}`,
        { method: "DELETE", headers: await authHeaders() }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to delete partner");
      }
      setPartners((prev) => prev.filter((p) => p.id !== partner.id));
      Swal.fire("Deleted", `${partner.name} was removed`, "success");
    } catch (e) {
      Swal.fire("Could not delete", e.message, "error");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        {providerConfig && (
          <Card className="mb-3">
            <CardHeader>
              <h4 className="mb-0">Drug pricing provider</h4>
              <p className="text-muted mb-0 mt-1">
                System-wide provider for drug search and the free discount card.
                Applies immediately — no redeploy.
              </p>
            </CardHeader>
            <CardBody>
              <div className="d-flex flex-wrap align-items-end gap-3">
                <div style={{ minWidth: 320 }}>
                  <Label className="fw-semibold">Active provider</Label>
                  <Input
                    type="select"
                    value={selectedProvider}
                    onChange={(e) => setSelectedProvider(e.target.value)}
                  >
                    {(providerConfig.options || ["ddn", "scriptsave"]).map(
                      (opt) => (
                        <option key={opt} value={opt}>
                          {PROVIDER_LABELS[opt] || opt}
                        </option>
                      )
                    )}
                  </Input>
                </div>
                <Button
                  color="primary"
                  disabled={
                    savingProvider ||
                    !selectedProvider ||
                    selectedProvider === providerConfig.provider
                  }
                  onClick={saveProvider}
                >
                  {savingProvider ? "Switching…" : "Switch provider"}
                </Button>
                <div className="text-muted small ms-auto">
                  Currently{" "}
                  <strong>
                    {PROVIDER_LABELS[providerConfig.provider] ||
                      providerConfig.provider}
                  </strong>{" "}
                  ({SOURCE_LABELS[providerConfig.source] || providerConfig.source}
                  ).
                  {providerConfig.source === "env" && (
                    <>
                      {" "}
                      An env variable is set; switching here (DB) will override
                      it.
                    </>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
        )}

        <Card>
          <CardHeader className="d-flex justify-content-between align-items-start">
            <div>
              <h4 className="mb-0">Partner Integrations</h4>
              <p className="text-muted mb-0 mt-1">
                One row per partner integrating with BaxRx. Create a partner to
                mint its API key, rotate a key, and set each partner's{" "}
                <strong>default National</strong> — the parent used for brokers
                onboarded without a specific one.
              </p>
            </div>
            <Button
              color="primary"
              className="text-nowrap ms-3"
              onClick={() => setShowCreate((v) => !v)}
            >
              {showCreate ? "Cancel" : "New partner"}
            </Button>
          </CardHeader>

          {showCreate && (
            <CardBody className="border-bottom bg-light">
              <h5 className="mb-3">New partner</h5>
              <div className="row g-3">
                <div className="col-md-6">
                  <FormGroup>
                    <Label className="fw-semibold">Name</Label>
                    <Input
                      type="text"
                      placeholder="e.g. Avero Health"
                      value={form.name}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, name: e.target.value }))
                      }
                    />
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label className="fw-semibold">
                      Slug <span className="text-muted">(optional)</span>
                    </Label>
                    <Input
                      type="text"
                      placeholder="e.g. avero"
                      value={form.slug}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, slug: e.target.value }))
                      }
                    />
                  </FormGroup>
                </div>
              </div>

              <FormGroup>
                <Label className="fw-semibold d-block">Scopes</Label>
                {ACTIVE_SCOPES.map((s) => (
                  <FormGroup check inline key={s.value} className="me-3 mb-1">
                    <Input
                      type="checkbox"
                      id={`scope-${s.value}`}
                      checked={form.scopes.includes(s.value)}
                      onChange={() => toggleScope(s.value)}
                    />
                    <Label check for={`scope-${s.value}`}>
                      {s.label}
                    </Label>
                  </FormGroup>
                ))}
              </FormGroup>

              <div className="mt-2">
                <Button
                  color="success"
                  disabled={creating}
                  onClick={createPartner}
                >
                  {creating ? "Creating…" : "Create partner & mint key"}
                </Button>
              </div>
            </CardBody>
          )}

          <CardBody>
            {loading ? (
              <div className="text-center py-4">
                <Spinner color="primary" />
              </div>
            ) : partners.length === 0 ? (
              <p className="text-muted mb-0">No partners configured yet.</p>
            ) : (
              <div className="table-responsive">
                <Table className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Partner</th>
                      <th>Status</th>
                      <th>API key</th>
                      <th>Provisioned</th>
                      <th style={{ minWidth: 240 }}>Default National</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partners.map((p) => {
                      const rotated = fmtDate(p.rotatedAt);
                      const created = fmtDate(p.createdAt);
                      const used = fmtDate(p.lastUsedAt);
                      return (
                        <tr key={p.id}>
                          <td>
                            <div className="fw-semibold">
                              <Link to={`/admin/partner-integrations/${p.id}`}>
                                {p.name}
                              </Link>
                              {p.slug ? (
                                <span className="badge bg-light text-muted ms-2">
                                  {p.slug}
                                </span>
                              ) : null}
                            </div>
                            <div className="text-muted small">{p.id}</div>
                          </td>
                          <td>
                            <span
                              className={`badge ${
                                p.active
                                  ? "bg-success-subtle text-success"
                                  : "bg-danger-subtle text-danger"
                              }`}
                            >
                              {p.active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td>
                            {p.hasKey ? (
                              <>
                                <span className="badge bg-success-subtle text-success">
                                  On file
                                </span>
                                <div className="text-muted small mt-1">
                                  {rotated
                                    ? `Rotated ${rotated}`
                                    : created
                                    ? `Created ${created}`
                                    : null}
                                  {used ? ` · used ${used}` : " · never used"}
                                </div>
                              </>
                            ) : (
                              <span className="badge bg-secondary-subtle text-secondary">
                                No key
                              </span>
                            )}
                          </td>
                          <td>
                            {p.deletable ? (
                              <span className="text-muted small">Nothing</span>
                            ) : (
                              <span className="small">
                                {p.groupsCount} group
                                {p.groupsCount === 1 ? "" : "s"},{" "}
                                {p.employeesCount} employee
                                {p.employeesCount === 1 ? "" : "s"}
                              </span>
                            )}
                          </td>
                          <td>
                            <Input
                              type="select"
                              bsSize="sm"
                              value={selections[p.id] || ""}
                              onChange={(e) =>
                                setSelections((s) => ({
                                  ...s,
                                  [p.id]: e.target.value,
                                }))
                              }
                            >
                              <option value="">— None —</option>
                              {nationals.map((n) => (
                                <option key={n.id} value={n.id}>
                                  {nationalName(n)}
                                </option>
                              ))}
                            </Input>
                          </td>
                          <td className="text-end text-nowrap">
                            <Link
                              to={`/admin/partner-integrations/${p.id}`}
                              className="btn btn-sm btn-soft-primary me-2"
                            >
                              Members
                            </Link>
                            <Button
                              size="sm"
                              color="primary"
                              className="me-2"
                              disabled={
                                savingId === p.id ||
                                (selections[p.id] || "") ===
                                  (p.defaultNationalId || "")
                              }
                              onClick={() => save(p.id)}
                            >
                              {savingId === p.id ? "Saving…" : "Save"}
                            </Button>

                            <Button
                              size="sm"
                              color="info"
                              outline
                              className="me-2"
                              disabled={busyId === p.id}
                              title={
                                p.hasKey
                                  ? "Issue a new key and invalidate the current one"
                                  : "Issue this partner's first key"
                              }
                              onClick={() => rotateKey(p)}
                            >
                              {p.hasKey ? "Rotate key" : "Issue key"}
                            </Button>

                            <Button
                              size="sm"
                              color={p.active ? "warning" : "success"}
                              outline
                              className="me-2"
                              disabled={busyId === p.id}
                              onClick={() => toggleActive(p)}
                            >
                              {p.active ? "Deactivate" : "Activate"}
                            </Button>

                            {/* Deleting a partner that provisioned anything would
                                orphan its groups and employees — records pointing
                                at a partner that no longer exists, with nobody
                                able to manage them. Don't offer what we'd refuse. */}
                            <Button
                              size="sm"
                              color="danger"
                              outline
                              disabled={!p.deletable || busyId === p.id}
                              title={
                                p.deletable
                                  ? "Delete this partner"
                                  : `Cannot delete — it provisioned ${p.groupsCount} group(s) and ${p.employeesCount} employee(s). Deleting would orphan them. Deactivate instead.`
                              }
                              onClick={() => remove(p)}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>

      <KeyRevealModal data={keyReveal} onClose={() => setKeyReveal(null)} />
    </div>
  );
};

export default PartnerIntegrations;
